import { api } from '../core';
import {
  BUNDLE_SECTIONS_SUGGESTIONS_CATEGORY_LIST_TAG,
  BUNDLE_SECTIONS_SUGGESTIONS_SUB_CATEGORY_LIST_TAG,
  BUNDLE_UNDO_REDO_HISTORY_TAG,
} from '../constants';
import {
  RootState,
  setBundleSections,
  setBundleSectionsSuggestionsCategoryList,
  setBundleSectionsSuggestionsSubCategoryList,
} from '@/store';
import { renameSectionsTreeNodeById } from '@repo/atomic-components';
import { BundleSectionsTreeNode } from './getBundleSections';

interface RenameBundleSectionsResponse {
  newNodeId: string | null;
}

interface RenameBundleSectionsProps {
  bundleId: string;
  abortController?: AbortController;
  isSubCategory: boolean;
  categoryId: string | null;
  categoryName: string | null;
  bundleSections: BundleSectionsTreeNode[];
  nodeId: string;
  newCategoryNameLocalStateUsage: string;
  newCategoryIdLocalStateUsage: string;
}

const renameBundleSectionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    renameBundleSections: builder.mutation<RenameBundleSectionsResponse, RenameBundleSectionsProps>({
      query: ({
        bundleId,
        abortController = new AbortController(),
        nodeId,
        isSubCategory,
        categoryId,
        categoryName,
      }: RenameBundleSectionsProps) => ({
        url: 'bundle_page/category/reassign',
        method: 'POST',
        signal: abortController.signal,
        body: {
          bundleId: bundleId,
          nodeId: nodeId,
          is_sub_category: isSubCategory,
          category_id: categoryId,
          category_name: categoryName,
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const state = getState() as RootState;

          if (
            arg.bundleSections &&
            arg.nodeId &&
            arg.newCategoryNameLocalStateUsage &&
            state.sharedData.activeBundleType
          ) {
            const renamedBundleSections = renameSectionsTreeNodeById(
              arg.bundleSections,
              arg.nodeId,
              arg.newCategoryNameLocalStateUsage,
              data.newNodeId ?? arg.newCategoryIdLocalStateUsage,
            );

            dispatch(
              setBundleSections({
                key: state.sharedData.activeBundleType,
                data: renamedBundleSections,
              }),
            );

            if (data.newNodeId) {
              if (arg.isSubCategory) {
                dispatch(
                  setBundleSectionsSuggestionsSubCategoryList({
                    key: state.sharedData.activeBundleType,
                    data: [
                      ...state.bundleSectionsSuggestions[state.sharedData.activeBundleType].subCategoryList,
                      {
                        id: data.newNodeId,
                        name: arg.newCategoryNameLocalStateUsage,
                      },
                    ],
                  }),
                );
              } else {
                dispatch(
                  setBundleSectionsSuggestionsCategoryList({
                    key: state.sharedData.activeBundleType,
                    data: [
                      ...state.bundleSectionsSuggestions[state.sharedData.activeBundleType].categoryList,
                      {
                        id: data.newNodeId,
                        name: arg.newCategoryNameLocalStateUsage,
                      },
                    ],
                  }),
                );
              }
            }
          }

          dispatch(
            api.util.invalidateTags([
              { type: BUNDLE_UNDO_REDO_HISTORY_TAG, id: arg.bundleId },
              // A rename can create a new category/sub-category on the
              // backend; refetch both lists so the local suggestions stay
              // in sync and later renames send the real id instead of a
              // name that already exists on BE.
              BUNDLE_SECTIONS_SUGGESTIONS_CATEGORY_LIST_TAG,
              BUNDLE_SECTIONS_SUGGESTIONS_SUB_CATEGORY_LIST_TAG,
            ]),
          );
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in renameBundleSections in onQueryStarted:', error);
          }
        }
      },
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useRenameBundleSectionsMutation } = renameBundleSectionsApi;
